/**
 * ChatKit Widget
 *
 * OpenAI ChatKit integration for Juno application.
 *
 * Structure:
 * - index.js - Entry point with initApps-compatible ChatKit class
 * - widget.js - Main widget logic (initialization, toggle, cleanup)
 * - configs.js - Widget type configurations (prompts, placeholders, etc.)
 * - helpers.js - Utility functions (data attribute parsing)
 * - chatkit.css - Isolated widget styles
 *
 * Available Widget Types (see configs.js):
 * - channel_admin: Default for admin users, full-featured assistant
 * - employee_import: For employee import flow with preview_id context
 *
 * Usage:
 *
 * 1. Global Widget (toggle button in corner):
 *    Rendered via `layouts/shared/_openai_chatkit.html.slim`.
 *    Container ID: `chatkit-widget-container`
 *    Hide with: `content_for :hide_chatkit_toggle`
 *
 * 2. Inline Widget (embedded in page):
 *    Container ID must end with `-chat-widget` for auto-initialization.
 *
 *    ```slim
 *    #employee-import-chat-widget[
 *      data-chatkit-type="employee_import"
 *      data-chatkit-preview-id=@preview.id
 *    ]
 *      openai-chatkit
 *    ```
 *
 * Context from data attributes:
 * All `data-chatkit-*` attributes (except `data-chatkit-type`) are passed to backend:
 * - `data-chatkit-preview-id="123"` -> `{ preview_id: "123" }`
 * - `data-chatkit-group-id="456"` -> `{ group_id: "456" }`
 *
 * Adding New Widget Types:
 *
 * 1. Add configuration to `configs.js`:
 *    ```js
 *    my_new_type: {
 *      context: { flow: 'my_new_type' },
 *      placeholder: 'Ask me anything...',
 *      attachments: { enabled: true, maxCount: 5, maxSize: 10485760 },
 *      greeting: 'Hello! How can I help?',
 *      prompts: [
 *        { label: 'Button text', prompt: 'Prompt sent to AI' }
 *      ]
 *    }
 *    ```
 *
 * 2. Use in HTML (ID must end with `-chat-widget`):
 *    ```slim
 *    #my-new-type-chat-widget[data-chatkit-type="my_new_type"]
 *      openai-chatkit
 *    ```
 */
import './widget';
import './chatkit.css';

export default class ChatKit {
  init() {
    if (document.getElementById('chatkit-widget-container')) {
      window.ChatKitWidget.setupToggleWidget();
    }

    document.querySelectorAll('[id$="-chat-widget"][data-chatkit-type]').forEach((widget) => {
      window.ChatKitWidget.initializeWidget(widget);
    });
  }
}
